import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDrawerMode, MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { SgAuxRouter } from '@sg/ui-kit/aux-route-bridge';
import { KillOnDestroy } from '@sg/utils';
import { BehaviorSubject } from 'rxjs';
import { skip } from 'rxjs/operators';
import { EnvironmentService } from '../services';
import { SgSidenavService } from './sidenav.service';

@Component({
  selector: 'sg-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
@KillOnDestroy()
export class SgSidenavComponent implements OnInit, OnChanges, AfterViewInit {
  /**
   * Viewport breakpoint at which the sidenav flips into mobile mode.
   * Defaults to `599px` to match the long-standing behavior every app
   * (admin, lender, borrower, analyzer, …) was built against. The chat
   * surface opts into a wider `767px` cutoff so tablets get the
   * mobile drawer treatment, but every other consumer keeps the old
   * threshold and isn't affected.
   */
  @Input() mobileBreakpointPx = 599;

  /**
   * Drawer mode on mobile viewports. `'push'` is the historical
   * behavior (drawer slides content sideways); `'over'` overlays the
   * page with a backdrop, which is what the chat surface uses. Default
   * stays `'push'` so non-chat apps keep their existing layout.
   */
  @Input() mobileMode: 'push' | 'over' = 'push';

  /**
   * Render a chat-style burger button in the top-left when in mobile
   * mode. Default off — apps that already have their own mobile menu
   * button (admin layout header, lender dashboard, …) would otherwise
   * show two competing buttons. The chat layout enables this.
   */
  @Input() showMobileBurger = false;

  /**
   * On mobile, force the drawer closed + expanded after init. Without
   * this the dark navbar still pushes content rightward and the menu
   * shows icons-only. Default off so non-chat apps render unchanged;
   * the chat layout opts in.
   */
  @Input() closeOnMobileInit = false;

  private mediaMatcher: MediaQueryList = matchMedia(`(max-width: ${this.mobileBreakpointPx}px)`);

  @Input() expenderEnabled: boolean;
  @Input() backgroundImageSrc: string;
  @Output() expandChange = new EventEmitter<boolean>();

  @ViewChild('sidenavContainer', { static: true }) sidenavContainer: MatSidenavContainer;
  @ViewChild('lSidenav', { static: true }) lSidenav: MatSidenav;
  @ViewChild('rSidenav', { static: true }) rSidenav: MatSidenav;

  mode: MatDrawerMode;
  isSmallScreen: boolean;
  isLoading = true;
  isExpanded: boolean = this.getInitialIsExpanded();
  initialSideNavMode$: BehaviorSubject<MatDrawerMode> = this.getInitialSideNavMode();
  disableCloseSideNav: boolean = this.getDisableCloseSideNav();
  showLogo: boolean = this.env.branding.showLogo;
  showAppName: boolean = this.env.branding.showAppName;
  logoSrc: string = this.env.branding.navBarLogoSrc;
  appName: string = this.env.branding.appName;
  expanderPosition = this.env.branding.expanderPosition;
  isFloatingSideBar = this.env.branding.isFloatingSideBar;
  showUserMenu = this.env.branding.showUserMenu;
  showNotifications = this.env.branding.showNotifications;
  navBarHomeLink = this.env.branding.navBarHomeLink || null;

  constructor(
    private router: Router,
    auxRouter: SgAuxRouter,
    private zone: NgZone,
    private env: EnvironmentService,
    public sidenavService: SgSidenavService
  ) {
    this.initMediaMatcher();
    this.isSmallScreen = this.mediaMatcher.matches;
    this.setMode();

    auxRouter.events.pipe(KillOnDestroy(this)).subscribe((event) => {
      if (event.outlet === 'sgMainSidenav') {
        if (event.kind === 'createOrUpdate') {
          this.togglePanel(true);
        } else {
          this.togglePanel(false);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.sidenavService.sidenavContainer$.next(this.sidenavContainer);
    this.sidenavService.leftSidenavComponent$.next(this);

    // Opt-in mobile-init behavior. Off by default to preserve the
    // long-standing layout every non-chat consumer relies on.
    if (this.closeOnMobileInit && this.isSmallScreen) {
      this.lSidenav.close();
      this.isExpanded = true;
      this.expandChange.emit(true);
    }

    setTimeout(() => {
      this.isLoading = false;
    }, 1000);
  }

  ngOnInit() {
    this.router.events.subscribe(() => {
      if (this.isSmallScreen) {
        this.lSidenav.close();
      }
    });

    this.sidenavService.toggleLeftSidenavComponent$.pipe(skip(1), KillOnDestroy(this)).subscribe((toggle?: boolean) => {
      this.toggleMenu(toggle);
    });
  }

  /**
   * Re-evaluate breakpoint / drawer-mode when their `@Input()`s change
   * after construction — the shell binds `mobileBreakpointPx` and
   * `mobileMode` to a route-aware observable, so the sidenav has to
   * react when the user navigates into or out of the chat surface
   * without re-mounting the component.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // NOT `&& !changes[...].firstChange`. `mediaMatcher` is built in a class
    // field initializer, which runs during construction — before Angular has
    // written any bound @Input — so it is always built against the 599px
    // default. The FIRST `ngOnChanges` call is exactly the one that carries
    // the real bound value (the shell's `isOnChatRoute$` uses `startWith(...)`,
    // so 767px is present on that first change), so skipping it left the
    // widened chat breakpoint applying only on in-SPA navigation, never on a
    // direct load or refresh of /sg-lender/chat. Same reasoning for
    // `mobileMode`: `setMode()` in the constructor also runs before the input
    // is set.
    if (changes['mobileBreakpointPx']) {
      this.mediaMatcher.removeEventListener('change', this.mediaMatcherOnchange);
      this.mediaMatcher = matchMedia(`(max-width: ${this.mobileBreakpointPx}px)`);
      this.mediaMatcher.addEventListener('change', this.mediaMatcherOnchange);
      this.isSmallScreen = this.mediaMatcher.matches;
      this.setMode();
    }
    if (changes['mobileMode']) {
      this.setMode();
    }
  }

  getInitialIsExpanded() {
    if (this.env.branding.sideMenuInitialState !== null && this.env.branding.sideMenuInitialState !== undefined) {
      return this.env.branding.sideMenuInitialState === 'opened';
    }
    return !JSON.parse(localStorage.getItem('isExpanded'));
  }

  getInitialSideNavMode(): BehaviorSubject<MatDrawerMode> {
    if (!!this.env.branding.initialSideNavMode) {
      return this.env.branding.initialSideNavMode;
    }
    return new BehaviorSubject('over');
  }

  getDisableCloseSideNav() {
    return this.env.branding.disableCloseSideNav ?? true;
  }

  onRightSidenavOpenedChange(value: boolean): void {
    // Only clear the sgMainSidenav outlet when it is actually active.
    // In Angular Material 21, the sidenav fires openedChange(false) during initialization
    // (when it renders in closed state), which would supersede the initial app navigation
    // if we called router.navigate unconditionally.
    if (!value && this.router.url.includes('sgMainSidenav')) {
      this.router.navigate(
        [
          {
            outlets: {
              sgMainSidenav: null,
            },
          },
        ],
        {
          queryParamsHandling: 'preserve',
        }
      );
    }
  }

  toggleMenu(toggle?: boolean): void {
    switch (toggle) {
      case true:
        this.lSidenav.open();
        break;
      case false:
        this.lSidenav.close();
        break;
      default:
        this.lSidenav.toggle();
        break;
    }
  }

  togglePanel(isOpen?: boolean): void {
    this.rSidenav.toggle(isOpen);
  }

  toggleExpended(value?: boolean): void {
    if (this.isSmallScreen) {
      value = true;
    }

    localStorage.setItem('isExpanded', this.isExpanded ? 'true' : 'false');

    switch (value) {
      case false:
      case true:
        if (this.isExpanded !== value) {
          this.isExpanded = value;
          this.expandChange.emit(this.isExpanded);
        }
        break;
      default:
        this.isExpanded = !this.isExpanded;
        this.expandChange.emit(this.isExpanded);
        break;
    }
  }

  private initMediaMatcher() {
    this.sidenavService.mediaMatcher$.pipe(KillOnDestroy(this)).subscribe((mediaMatcher: MediaQueryList) => {
      if (!mediaMatcher) return;
      this.mediaMatcher.removeEventListener('change', this.mediaMatcherOnchange);
      this.mediaMatcher = mediaMatcher;
      this.mediaMatcher.addEventListener('change', this.mediaMatcherOnchange);
    });
    this.mediaMatcher.addEventListener('change', this.mediaMatcherOnchange);
  }

  private mediaMatcherOnchange = (event: MediaQueryListEvent) => {
    return this.zone.run(() => {
      if (this.isSmallScreen !== event.matches) {
        this.isSmallScreen = event.matches;
        this.setMode();

        if (this.isSmallScreen) {
          this.toggleExpended(true);
          this.lSidenav.close();
        } else {
          this.lSidenav.open();
        }
      }
    });
  };

  private setMode(): void {
    // Mobile mode is `@Input()`-driven (default `'push'`) so existing
    // consumers keep their old layout; the chat surface opts in to
    // `'over'` (drawer overlays the page with a backdrop) to avoid the
    // permanent dead band push leaves on small screens.
    this.mode = this.isSmallScreen ? this.mobileMode : 'side';
  }
}
